<template>
  <div class="table-wrapper">
    <v-btn
      small
      class="mb-2"
      :color="useAlternate ? 'amber darken-2' : 'light-blue darken-2'"
      style="font-weight:600; text-transform:none; padding:0 12px;"
      @click="toggleUseAlternate"
    >
      <v-icon left>
        {{ useAlternate ? "mdi-file-document-edit-outline" : "mdi-satellite-variant" }}
      </v-icon>
      {{ useAlternate ? "Last Noon Data" : "Telemetry Data" }}
    </v-btn>

    <v-btn
      small
      color="secondary"
      class="mb-2"
      @click="resetFilters"
    >
      Clear Filters
    </v-btn>

    <table class="custom-table">
      <thead>
        <tr>
          <th
            v-for="header in headers"
            :key="header.value"
          >
            <TableHeaderCell
              :text="header.text"
              :value="header.value"
              :sortable="header.sortable"
              :is-sorted-desc="sortDesc && sortKey === header.value"
              :filter-options="getFilterOptions(header.value)"
              :data-icon="getDataIcon(header.value)"
              :data-icon-color="getDataIconColor(header.value)"
              :data-icon-title="getDataIconTitle(header.value)"
              @sort-change="toggleSort"
              @filter-change="onFilterChanged(header.value, $event)"
            />
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="item in sortedVessels"
          :key="item.name"
        >
          <td class="vessel-name-cell">
            <v-btn
              text
              color="white"
              class="vessel-name-btn"
              @click="$emit('open-drawer', item.name)"
            >
              {{ item.name }}
            </v-btn>
          </td>

          <td>{{ displaySailingCondition(item) }}</td>

          <td>
            {{ displaySpeed(item.vesselSpeed) }}
          </td>

          <td>
            <FocHover
              :foc-data="item.lastDayFOC"
              position="right"
              :offset-x="8"
            />
          </td>

          <td>
            <v-chip :class="getCiiColor(item.cii, item)">
              {{ displayCii(item.cii, item) }}
            </v-chip>
          </td>

          <td>
            <v-chip :class="foulingClass(item.fouling)">
              {{ item.fouling != null ? parseFloat(item.fouling).toFixed(1) + "%" : "-" }}
            </v-chip>
          </td>

          <td>
            <v-chip :class="cleaningClass(item.daysSinceCleaning)">
              {{ item.daysSinceCleaning != null ? item.daysSinceCleaning + " days" : "-" }}
            </v-chip>
          </td>

          <td>
            <v-menu bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  v-bind="attrs"
                  :class="DataIssuesClass(item.sensorIssues.length)"
                  class="sensor-issues-chip"
                  v-on="on"
                >
                  {{ item.sensorIssues.length }}
                </v-chip>
              </template>

              <v-card
                class="alert-card"
                elevation="6"
              >
                <v-list dense>
                  <v-list-item
                    v-for="(alert, index) in item.sensorIssues"
                    :key="index"
                    class="alert-item"
                  >
                    <template v-slot:prepend>
                      <v-icon
                        :color="alert.checkValue > alert.limit ? 'red lighten-2' : 'amber lighten-2'"
                        size="20"
                      >
                        mdi-gauge
                      </v-icon>
                    </template>

                    <v-list-item-content>
                      <v-list-item-title class="alert-title">
                        {{ alert.sensor }}
                      </v-list-item-title>

                      <v-list-item-subtitle class="alert-subtitle">
                        {{ alert.type }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </td>

          <td>
            <v-chip :class="dateClass(item.latestFDMUpload)">
              {{ formatUtcDate(item.latestFDMUpload) + " (UTC)" || "-" }}
            </v-chip>
          </td>

          <td v-if="showNoonColumn && item.latestNoonReportDate">
            <v-chip :class="dateClass(item.latestNoonReportDate)">
              {{ formatUtcDate(item.latestNoonReportDate) + " (UTC)" }}
            </v-chip>
          </td>

          <td v-else-if="showNoonColumn">
            -
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import FocHover from "./FocHover.vue";
import TableHeaderCell from "./TableHeaderCell.vue";

import {
  formatUtcDate,
  displaySpeed,
  displayCii,
  getCiiColor,
  foulingClass,
  cleaningClass,
  dateClass,
  dataIssuesClass,
} from "@/helper_functions/home2.js";

export default {
  name: "FleetTable",

  components: {
    FocHover,
    TableHeaderCell,
  },

  data() {
    return {
      useAlternate: false,
      sortDesc: true,
      sortKey: "cii",
      ciiFilter: "All",
      ciiGrades: ["A", "B", "C", "D"],
      sailingFilter: "All",
      dataIssueFilter: "All",
    };
  },

  computed: {
    ...mapGetters("fleet", ["vessels"]),

    showNoonColumn() {
      return true;

    },

    hasNoonSailingCondition() {
      return this.vessels.some((v) => v.NoonsailingCondition);
    },

    headers() {
      const baseHeaders = [
        { text: "Vessel", value: "name", sortable: true },
        { text: "Sailing Condition", value: "sailingCondition", sortable: false },
        { text: "Vessel Speed", value: "vesselSpeed", sortable: true },
        { text: "Daily FOC", value: "lastDayFOC", sortable: true },
        { text: "CII", value: "cii", sortable: true },
        { text: "Fouling", value: "fouling", sortable: true },
        { text: "Last Cleaning Event", value: "daysSinceCleaning", sortable: true },
        { text: "Data Issues", value: "sensorIssues", sortable: true },
        { text: "Latest Telemetry Data", value: "latestFDMUpload", sortable: true },
      ];

      if (this.showNoonColumn) {
        baseHeaders.push({
          text: "Latest Noon Report",
          value: "latestNoonReportDate",
          sortable: true,
        });
      }

      return baseHeaders;
    },

    safeVessels() {
      return Array.isArray(this.vessels) ? this.vessels : [];
    },

    normalizedVessels() {
      return this.safeVessels.map((vessel) => {
        const focRaw = this.useAlternate ? vessel.lastDayFOC : vessel.lastDayFOC2;

        let adjustedFoc = focRaw;

        if (focRaw && focRaw.total > 150) {
          adjustedFoc = {
            ...focRaw,
            total: focRaw.total / 1000,
            compBoiler: focRaw.compBoiler,
          };
        }

        return {
          ...vessel,
          vesselSpeed: this.useAlternate ? vessel.vesselSpeed1 : vessel.vesselSpeed,
          lastDayFOC: adjustedFoc,
          sailingCondition:
            this.useAlternate && this.hasNoonSailingCondition
              ? vessel.NoonsailingCondition
              : vessel.sailingCondition,
          ciiLetter: vessel.ciiLetter,
        };
      });
    },

    sailingStatusOptions() {
      return this.useAlternate
        ? [
          "All",
          "Sailing",
          "End of Sea Passage",
          "Arrival",
          "Anchored - At Port",
          "Departing",
          "Beginning of Sea Passage",
        ]
        : ["All", "Sailing", "Anchored - At Port"];
    },

    sortedVessels() {
      let vessels = [...this.normalizedVessels];

      vessels = this.applyCiiFilter(vessels);
      vessels = this.applySailingFilter(vessels);
      vessels = this.applyDataIssuesFilter(vessels);

      return vessels.sort(this.compareVessels);
    },
  },

  methods: {
    formatUtcDate,
    displaySpeed,
    displayCii,
    getCiiColor,
    foulingClass,
    cleaningClass,
    dateClass,
    DataIssuesClass: dataIssuesClass,

    toggleUseAlternate() {
      this.useAlternate = !this.useAlternate;
      this.sailingFilter = "All";
    },

    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc;
        return;
      }

      this.sortKey = key;
      this.sortDesc = true;
    },

    resetFilters() {
      this.ciiFilter = "All";
      this.sailingFilter = "All";
      this.dataIssueFilter = "All";
    },

    onFilterChanged(headerValue, selectedValue) {
      if (headerValue === "cii") {
        this.ciiFilter = selectedValue;
        return;
      }

      if (headerValue === "sailingCondition") {
        this.sailingFilter = selectedValue;
        return;
      }

      if (headerValue === "sensorIssues") {
        this.dataIssueFilter = selectedValue;
      }
    },

    getFilterOptions(headerValue) {
      if (headerValue === "cii") {
        return this.ciiGrades;
      }

      if (headerValue === "sailingCondition" && this.hasNoonSailingCondition) {
        return this.sailingStatusOptions.filter((option) => option !== "All");
      }

      if (headerValue === "sensorIssues") {
        return ["green", "yellow", "red"];
      }

      return [];
    },

    getDataIcon(headerValue) {
      const usesDataSourceIcon =
        headerValue === "vesselSpeed" ||
        headerValue === "lastDayFOC" ||
        (headerValue === "sailingCondition" && this.hasNoonSailingCondition);

      if (!usesDataSourceIcon) {
        return "";
      }

      return this.useAlternate
        ? "mdi-file-document-edit-outline"
        : "mdi-satellite-variant";
    },

    getDataIconColor(headerValue) {
      if (!this.getDataIcon(headerValue)) {
        return "";
      }

      return this.useAlternate ? "amber" : "light-blue lighten-2";
    },

    getDataIconTitle(headerValue) {
      if (!this.getDataIcon(headerValue)) {
        return "";
      }

      return this.useAlternate ? "Noon Report Data" : "Telemetry Data";
    },

    displaySailingCondition(item) {
      return item.sailingCondition || "-";
    },

    applyCiiFilter(vessels) {
      if (this.ciiFilter === "All") {
        return vessels;
      }

      return vessels.filter((vessel) => {
        const match = displayCii(vessel.cii, vessel).match(/\((\w)\)/);
        const letter = match ? match[1] : null;

        return letter === this.ciiFilter;
      });
    },

    applySailingFilter(vessels) {
      if (this.sailingFilter === "All") {
        return vessels;
      }

      return vessels.filter((vessel) => vessel.sailingCondition === this.sailingFilter);
    },

    applyDataIssuesFilter(vessels) {
      if (this.dataIssueFilter === "All") {
        return vessels;
      }

      return vessels.filter((vessel) =>
        this.DataIssuesClass(vessel.sensorIssues.length).includes(this.dataIssueFilter)
      );
    },

    compareVessels(a, b) {
      let valA = a[this.sortKey];
      let valB = b[this.sortKey];

      if (this.sortKey === "lastDayFOC") {
        valA = valA ? valA.total : 0;
        valB = valB ? valB.total : 0;
      } else if (this.sortKey === "sensorIssues") {
        valA = valA.length;
        valB = valB.length;
      } else if (
        this.sortKey === "latestFDMUpload" ||
        this.sortKey === "latestNoonReportDate"
      ) {
        valA = new Date(valA).getTime() || 0;
        valB = new Date(valB).getTime() || 0;
      } else if (typeof valA === "string") {
        valA = valA.toLowerCase();
        valB = valB ? valB.toLowerCase() : "";
      }

      if (valA < valB) return this.sortDesc ? 1 : -1;
      if (valA > valB) return this.sortDesc ? -1 : 1;

      return 0;
    },

  },
};
</script>

<style scoped>
.table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  padding: 16px;
  background-color: #1b2b44;
}

.custom-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  color: #f0f4f8;
  font-family: Arial, sans-serif;
}

.custom-table th {
  background: #1a2540;
  padding: 12px 8px;
  border-bottom: 2px solid #374a65;
  text-align: center;
  color: #f0f4f8;
}

.custom-table td {
  background: #152339;
  padding: 12px 8px;
  border-bottom: 1px solid #2c3d5a;
  text-align: center;
}

.custom-table tbody tr:hover td {
  background: #243757;
}

.vessel-name-cell {
  text-align: center;
}

.vessel-name-btn {
  border: 1px solid #fff;
  border-radius: 8px;
  background-color: #2a4d6e;
  color: #fff;
  transition: all 0.2s ease;
  text-transform: none;
  font-weight: bold;
  font-size: 11px;
  width: 165px;
  height: 28px;
  padding: 4px 6px;
}

.vessel-name-btn:hover {
  background-color: #3b6b90;
  transform: scale(1.05);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
}

.sensor-issues-chip {
  cursor: pointer;
}

.alert-card {
  background-color: #1b2b44;
  color: #f0f4f8;
  border-radius: 8px;
}

.alert-item {
  min-height: 40px;
}

.alert-title {
  color: #ffffff;
  font-size: 13px;
}

.alert-subtitle {
  color: #cfd8dc;
  font-size: 12px;
}

.v-chip {
  border-radius: 16px;
  padding: 2px 8px;
  font-size: 12px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.4);
}

.v-chip.green {
  background: #39533f !important;
  color: #00c746 !important;
}

.v-chip.yellow {
  background: #534f3e !important;
  color: #ffd000 !important;
}

.v-chip.red {
  background: #5e4142 !important;
  color: #ff0000 !important;
}

.v-chip.grey {
  background: #6c757d !important;
  color: #f8f9fa !important;
}

.v-chip.ciigreen {
  background: rgba(10, 177, 10, 0.6) !important;
  color: #fff !important;
}

.v-chip.ciigreen2 {
  background: rgba(174, 236, 51, 0.6) !important;
  color: #fff !important;
}

.v-chip.ciiorange {
  background: rgba(255, 165, 0, 0.6) !important;
  color: #fff !important;
}

.v-chip.ciibrown {
  background: rgba(221, 128, 13, 0.6) !important;
  color: #f8f9fa !important;
}

.v-chip.ciired {
  background: rgba(221, 28, 13, 0.6) !important;
  color: #f8f9fa !important;
}

</style>
